<template>
  <div class="blog-detail-page">
    <div class="container py-4">
      <div v-if="loading" class="blog-detail-loading">
        <div class="skel skel-hero"></div>
        <div class="skel skel-line w-40"></div>
        <div class="skel skel-line w-90"></div>
        <div class="skel skel-line w-70"></div>
      </div>

      <div v-else-if="error" class="blog-state">
        <i class="pi pi-exclamation-triangle"></i>
        <p>تعذّر العثور على هذا المقال.</p>
        <router-link to="/blogs" class="blog-retry"><i class="pi pi-arrow-right"></i> العودة للمدونة</router-link>
      </div>

      <template v-else-if="post">
        <nav class="blog-breadcrumb">
          <router-link to="/blogs">المدونة</router-link>
          <i class="pi pi-angle-left"></i>
          <span>{{ post.title }}</span>
        </nav>

        <span v-if="post.category" class="blog-detail__category">{{ post.category.title }}</span>
        <h1 class="blog-detail__title">{{ post.title }}</h1>
        <div class="blog-detail__meta">
          <i class="pi pi-calendar"></i>
          <span>{{ date }}</span>
        </div>

        <img v-if="post.featured_image_link" :src="post.featured_image_link" :alt="post.title" class="blog-detail__hero" />

        <div class="blog-detail__content" v-html="post.content"></div>

        <!-- Related posts -->
        <section v-if="related.length" class="blog-related">
          <h3 class="blog-related__title">مقالات ذات صلة</h3>
          <div class="blog-related__grid">
            <BlogCard v-for="p in related" :key="p.id" :post="p" />
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import BlogCard from '@/components/blog/BlogCard.vue';
import { blogService } from '@/services/blogService.js';
import { applySeoMeta } from '@/composables/useSeoMeta';

export default {
  name: 'BlogDetail',
  components: { BlogCard },
  data() {
    return {
      post: null,
      related: [],
      loading: true,
      error: false,
    };
  },
  computed: {
    slug() { return this.$route.params.slug; },
    date() {
      if (!this.post?.published_at) return '';
      return new Date(this.post.published_at).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });
    },
  },
  watch: {
    slug() { this.load(); },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = false;
      try {
        const result = await blogService.post(this.slug);
        this.post = result.data;
        this.related = result.related ?? [];
        this.applyMeta();
      } catch {
        this.error = true;
        this.post = null;
      } finally {
        this.loading = false;
      }
    },
    applyMeta() {
      if (this._seoCleanup) this._seoCleanup();
      if (!this.post) return;
      this._seoCleanup = applySeoMeta({
        title: `${this.post.meta_title || this.post.title} | أ/ مصطفى حسني - مدرس الكيمياء`,
        description: this.post.meta_description || this.post.excerpt || '',
        canonical: `https://moustafahosney.com/blogs/${this.post.slug}`,
        image: this.post.og_image || undefined,
        type: 'article',
      });
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    if (this._seoCleanup) this._seoCleanup();
  },
};
</script>

<style scoped>
.blog-detail-page { min-height: 60vh; padding-bottom: 5rem; max-width: 780px; margin: 0 auto; }
.blog-breadcrumb {
  display: flex; align-items: center; gap: .4rem; font-size: .85rem; color: #6B7280; margin-bottom: 1.2rem;
}
.blog-breadcrumb a { color: #051629; text-decoration: none; font-weight: 700; }
.blog-detail__category {
  display: inline-block;
  background: rgba(5, 22, 41,.07); color: #051629;
  border-radius: 8px; padding: .25rem .65rem; font-size: .78rem; font-weight: 700;
  margin-bottom: .8rem;
}
.blog-detail__title { font-size: clamp(1.5rem, 3vw, 2.1rem); font-weight: 800; color: #111827; line-height: 1.4; margin-bottom: .6rem; }
.blog-detail__meta { display: flex; align-items: center; gap: .4rem; font-size: .84rem; color: #94a3b8; margin-bottom: 1.5rem; }
.blog-detail__hero { width: 100%; max-height: 420px; object-fit: cover; border-radius: 20px; margin-bottom: 2rem; }
.blog-detail__content { line-height: 1.9; color: #1F2937; font-size: 1.02rem; }
.blog-detail__content :deep(img) { max-width: 100%; border-radius: 12px; margin: 1rem 0; }
.blog-detail__content :deep(a) { color: #051629; font-weight: 700; }

.blog-related { margin-top: 3.5rem; padding-top: 2rem; border-top: 1px solid #E5E7EB; }
.blog-related__title { font-size: 1.15rem; font-weight: 800; color: #111827; margin-bottom: 1.2rem; }
.blog-related__grid { display: grid; gap: 1.1rem; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); }

.blog-state { text-align: center; padding: 3.5rem 1rem; color: #94a3b8; }
.blog-state .pi { font-size: 2.6rem; margin-bottom: .8rem; display: block; }
.blog-retry {
  display: inline-flex; align-items: center; gap: .4rem; margin-top: .5rem; background: #051629; color: #fff;
  border: none; border-radius: 10px; padding: .5rem 1.1rem; font-weight: 700; cursor: pointer; text-decoration: none;
}

.blog-detail-loading { padding: 1rem 0; }
.skel { background: #e9edf5; border-radius: 8px; position: relative; overflow: hidden; margin-bottom: .8rem; }
.skel::after {
  content: ''; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent);
  animation: skel-shimmer 1.3s infinite;
}
@keyframes skel-shimmer { 100% { transform: translateX(100%); } }
.skel-hero { aspect-ratio: 16/7; border-radius: 20px; }
.skel-line { height: 14px; }
.w-40 { width: 40%; } .w-70 { width: 70%; } .w-90 { width: 90%; }
</style>
